/**
 * FloatingChatProvider - Context for managing floating DM chat windows
 * Allows opening chat from anywhere in the app
 */
import React, { createContext, useContext, useState, useEffect } from 'react';
import FloatingChatWidget from './FloatingChatWidget';
import { useAuth } from '../context/AuthContext';
import { messagesApi } from '../api/messages';

const FloatingChatContext = createContext(null);

export const useFloatingChat = () => {
  const context = useContext(FloatingChatContext);
  if (!context) {
    throw new Error('useFloatingChat must be used within FloatingChatProvider');
  }
  return context;
};

export const FloatingChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [openChats, setOpenChats] = useState([]); // Array of { user, isMinimized, messages }
  const [unreadCount, setUnreadCount] = useState(0);
  const MAX_CHATS = 3; // Maximum number of open chat windows

  // Fetch unread count periodically
  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return;
    }

    const fetchUnread = async () => {
      try {
        const data = await messagesApi.getUnreadCount();
        setUnreadCount(data.count || 0);
      } catch (err) {
        // Silently fail
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 30000); // Every 30 seconds
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Open a new chat or focus existing one
  const openChat = (chatUser, prefill = '', product = null) => {
    if (!chatUser?.id || !user) return;
    
    // Check if chat already exists
    const existingIndex = openChats.findIndex(c => c.user.id === chatUser.id);
    
    if (existingIndex !== -1) {
      // Chat exists - expand it, refresh prefill/product context if provided
      setOpenChats(prev => prev.map((chat, idx) => 
        idx === existingIndex
          ? { ...chat, isMinimized: false, draft: prefill || chat.draft, product: product || chat.product }
          : chat
      ));
      return;
    }

    // Add new chat
    const newChat = {
      user: chatUser,
      isMinimized: false,
      messages: [],
      draft: prefill,
      product: product
    };

    setOpenChats(prev => {
      // If at max capacity, remove oldest minimized or oldest chat
      if (prev.length >= MAX_CHATS) {
        const minimizedIndex = prev.findIndex(c => c.isMinimized);
        if (minimizedIndex !== -1) {
          return [...prev.slice(0, minimizedIndex), ...prev.slice(minimizedIndex + 1), newChat];
        }
        return [...prev.slice(1), newChat];
      }
      return [...prev, newChat];
    });
  };

  // Close a chat
  const closeChat = (userId) => {
    setOpenChats(prev => prev.filter(c => c.user.id !== userId));
  };

  // Toggle minimize state
  const toggleMinimize = (userId) => {
    setOpenChats(prev => prev.map(chat => 
      chat.user.id === userId ? { ...chat, isMinimized: !chat.isMinimized } : chat
    ));
  };

  // Handle new message sent
  const handleNewMessage = (userId, message) => {
    // Could trigger notification here if needed
  };

  // Refresh unread count
  const refreshUnreadCount = async () => {
    if (!isAuthenticated) return;
    try {
      const data = await messagesApi.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch (err) {
      // Silently fail
    }
  };

  // Context value
  const contextValue = {
    openChat,
    closeChat,
    toggleMinimize,
    openChats,
    unreadCount,
    refreshUnreadCount
  };

  // Don't render chat widgets if not logged in
  if (!isAuthenticated) {
    return (
      <FloatingChatContext.Provider value={contextValue}>
        {children}
      </FloatingChatContext.Provider>
    );
  }

  return (
    <FloatingChatContext.Provider value={contextValue}>
      {children}
      
      {/* Floating Chat Windows Container */}
      <div 
        className="fixed bottom-36 right-4 flex items-end gap-3 z-30 pointer-events-none"
        data-testid="floating-chats-container"
      >
        {openChats.map((chat) => (
          <div key={chat.user.id} className="pointer-events-auto">
            <FloatingChatWidget
              chatUser={chat.user}
              isMinimized={chat.isMinimized}
              initialMessages={chat.messages}
              initialDraft={chat.draft}
              product={chat.product}
              onClose={() => closeChat(chat.user.id)}
              onMinimize={() => toggleMinimize(chat.user.id)}
              onNewMessage={handleNewMessage}
            />
          </div>
        ))}
      </div>
    </FloatingChatContext.Provider>
  );
};

export default FloatingChatProvider;
